import React, { useState } from "react";
import TimezoneSelect from "@/components/TimezoneSelect";
import { browserTimezone } from "@/lib/timezones";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Brand, ThemeToggle } from "@/components/common";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ChevronLeft, ChevronRight, Trophy, Repeat, GitBranch, Layers, Shuffle } from "lucide-react";

const TYPES = [
  { id: "single_elimination", name: "Single Elimination", icon: Trophy, desc: "Lose once and you're out. Auto-generated bracket.", ready: true },
  { id: "round_robin", name: "Round Robin", icon: Repeat, desc: "Everyone plays everyone. Ranked by points.", ready: true },
  { id: "group_elimination", name: "Group Stage + Elimination", icon: Layers, desc: "Groups play round robin, top teams advance to a knockout.", ready: true },
  { id: "double_elimination", name: "Double Elimination", icon: GitBranch, desc: "Two losses to be eliminated.", ready: false },
  { id: "swiss", name: "Swiss System", icon: Shuffle, desc: "Performance-based pairings each round.", ready: false },
];

const STEPS = ["Tournament type", "Settings", "Size & format"];

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const numberInput = (value) => value === "" ? "" : parseInt(value, 10);

export default function Wizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", date: "", start_time: "", location: "",
    timezone: browserTimezone(),
    logo_url: null,
    max_teams: 8, min_members: 1, max_members: 12, num_fields: 2,
    match_duration: 20, break_time: 5, tournament_type: "single_elimination",
    allow_captain_team_rename: false,
    allow_captain_logo_change: false,
    is_public: true,
    type_config: { double: false },
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectType = (id) => {
    setForm((f) => {
      let type_config = {};
      if (id === "round_robin") type_config = { double: false };
      else if (id === "group_elimination") type_config = { num_groups: 2, qualifiers: 2 };
      return { ...f, tournament_type: id, type_config };
    });
  };

  const canNext = () => {
    if (step === 1) return form.name.trim().length > 0;
    return true;
  };

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.date && form.start_time) payload.start_time = `${form.date}T${form.start_time}:00`;
      else payload.start_time = null;
      const { data } = await api.post("/tournaments", payload);
      toast.success("Tournament created");
      nav(`/host/t/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/70 px-5 py-3 backdrop-blur-xl">
        <Brand onClick={() => nav("/host")} />
        <ThemeToggle />
      </header>

      <div className="mx-auto max-w-2xl px-6 py-8">
        <div className="mb-8 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`text-sm font-medium ${i === step ? "" : "text-muted-foreground"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
            </React.Fragment>
          ))}
        </div>

        <Card className="p-6">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-settings">
              <h2 className="font-heading text-2xl font-bold">Settings</h2>
              <div>
                <Label>Tournament name *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5"
                  placeholder="Summer Cup 2026" data-testid="wizard-name-input" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" placeholder="City Sports Park" data-testid="wizard-location-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="wizard-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input type="date" min={today} value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" data-testid="wizard-date-input" />
                </div>
                <div>
                  <Label>Start time</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="mt-1.5" data-testid="wizard-time-input" />
                </div>
              </div>
              <TimezoneSelect value={form.timezone} onChange={(value) => set("timezone", value)} />
              <div>
                <Label>Tournament logo</Label>
                <div className="mt-1.5"><ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="wizard-logo-upload" /></div>
              </div>
              <div className="space-y-3 rounded-xl border p-4">
                <label className="flex items-start justify-between gap-4" data-testid="wizard-allow-captain-team-rename">
                  <span>
                    <span className="block font-medium">Allow captains to change their team name</span>
                    <span className="text-sm text-muted-foreground">Captains can rename only their own team from the captain page.</span>
                  </span>
                  <Switch checked={form.allow_captain_team_rename} onCheckedChange={(v) => set("allow_captain_team_rename", !!v)} />
                </label>
                <label className="flex items-start justify-between gap-4 border-t pt-3" data-testid="wizard-allow-captain-logo-change">
                  <span>
                    <span className="block font-medium">Allow captains to change their team logo</span>
                    <span className="text-sm text-muted-foreground">Captains can upload or remove their own team logo.</span>
                  </span>
                  <Switch checked={form.allow_captain_logo_change} onCheckedChange={(v) => set("allow_captain_logo_change", !!v)} />
                </label>
              </div>
              <label className="flex items-start gap-3 rounded-xl border p-4" data-testid="wizard-public-tournament">
                <Checkbox checked={form.is_public} onCheckedChange={(v) => set("is_public", !!v)} />
                <span>
                  <span className="block font-medium">Public tournament</span>
                  <span className="text-sm text-muted-foreground">Public tournaments appear in search. Private tournaments are accessible only through their direct URL.</span>
                </span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-size">
              <h2 className="font-heading text-2xl font-bold">Size & format</h2>
              <div className="grid grid-cols-2 gap-4">
                <NumField label="Max teams" value={form.max_teams} onChange={(v) => set("max_teams", v)} testId="wizard-maxteams" />
                <NumField label="Fields / courts" value={form.num_fields} onChange={(v) => set("num_fields", v)} testId="wizard-fields" />
                <NumField label="Min members / team" value={form.min_members} onChange={(v) => set("min_members", v)} testId="wizard-minmembers" />
                <NumField label="Max members / team" value={form.max_members} onChange={(v) => set("max_members", v)} testId="wizard-maxmembers" />
                <NumField label="Match duration (min)" value={form.match_duration} onChange={(v) => set("match_duration", v)} testId="wizard-duration" />
                <NumField label="Break time (min)" value={form.break_time} onChange={(v) => set("break_time", v)} testId="wizard-break" />
              </div>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-type">
              <h2 className="font-heading text-2xl font-bold">Tournament type</h2>
              <div className="grid gap-3">
                {TYPES.map((t) => (
                  <button
                    key={t.id} type="button" disabled={!t.ready}
                    onClick={() => t.ready && selectType(t.id)}
                    data-testid={`wizard-type-${t.id}`}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${form.tournament_type === t.id ? "border-primary bg-primary/5" : "border-border"} ${!t.ready ? "opacity-50" : "hover:border-primary/50"}`}
                  >
                    <t.icon className="mt-0.5 h-5 w-5 text-primary" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-semibold">
                        {t.name}
                        {!t.ready && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">SOON</span>}
                      </div>
                      <div className="text-sm text-muted-foreground">{t.desc}</div>
                    </div>
                    {form.tournament_type === t.id && <Check className="h-5 w-5 text-primary" />}
                  </button>
                ))}
              </div>
              {form.tournament_type === "round_robin" && (
                <div>
                  <Label>Rounds</Label>
                  <Select value={form.type_config.double ? "2" : "1"} onValueChange={(v) => set("type_config", { double: v === "2" })}>
                    <SelectTrigger className="mt-1.5" data-testid="wizard-rr-rounds"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Everyone plays once</SelectItem>
                      <SelectItem value="2">Everyone plays twice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.tournament_type === "group_elimination" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of groups</Label>
                    <Input type="number" min={2} max={8}
                      value={form.type_config.num_groups ?? 2}
                      onChange={(e) => set("type_config", { ...form.type_config, num_groups: numberInput(e.target.value) })}
                      onBlur={() => form.type_config.num_groups === "" && set("type_config", { ...form.type_config, num_groups: 2 })}
                      className="mt-1.5" data-testid="wizard-num-groups" />
                  </div>
                  <div>
                    <Label>Qualify per group</Label>
                    <Input type="number" min={1} max={4}
                      value={form.type_config.qualifiers ?? 2}
                      onChange={(e) => set("type_config", { ...form.type_config, qualifiers: numberInput(e.target.value) })}
                      onBlur={() => form.type_config.qualifiers === "" && set("type_config", { ...form.type_config, qualifiers: 1 })}
                      className="mt-1.5" data-testid="wizard-qualifiers" />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={() => (step === 0 ? nav("/host") : setStep(step - 1))} data-testid="wizard-back">
              <ChevronLeft className="mr-1 h-4 w-4" /> {step === 0 ? "Cancel" : "Back"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} data-testid="wizard-next">
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={create} disabled={saving} data-testid="wizard-create">
                {saving ? "Creating…" : "Create tournament"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" min={1} value={value} onChange={(e) => onChange(numberInput(e.target.value))}
        onBlur={() => value === "" && onChange(1)} className="mt-1.5" data-testid={testId} />
    </div>
  );
}
